import React, { useEffect, useState } from "react";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import { GetServerSideProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";

import { getCityStateGeocodeServerSide } from "api/requests/getCityStateGeocode";
import { getDeals } from "api/requests/getDeals";
import getPromotedStrains from "api/requests/getPromotedStrains";
import { getPromotions } from "api/requests/getPromotions";
import { Coordinates } from "custom-types/Coordinates";
import { Deal } from "custom-types/Deals";
import { PromotedStrain } from "custom-types/Directus";
import { Promotion } from "custom-types/Promotions";
import useDomain from "hooks/useDomain";
import logError from "utils/logError";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import DealsDisclaimer from "components/DealsDisclaimer";
import DealsHero from "components/DealsHero";
import DealsResults from "components/DealsResults";
import DealTypeCarousels from "components/DealTypeCarousels";
import PromoResults from "components/PromoResults";

class InvalidLocationError extends Error {}

const extractLocation = (slug: string): { city: string; state: string } => {
  const slugArr = slug.split("-");

  // length of 3 is city-state-country
  if (slugArr.length >= 3) {
    const state = slugArr[slugArr.length - 2].toUpperCase();
    const city = slugArr
      .slice(0, -2)
      .map((word: string) => capitalize(word))
      .join(" ");
    return { city, state };
  }

  // length of 2 is city-state or state-country
  if (slugArr.length === 2) {
    return { city: startCase(slugArr[0]), state: slugArr[1].toUpperCase() };
  }

  throw new InvalidLocationError(`invalid location slug: ${slug}`);
};

/** if country is missing in slug, append it to the
 *  end to use in SEO canonical URL */
const formatSlug = (slug: string, countryCode: string) => {
  const countryCodes = ["us", "ca"];
  const slugArr = slug.split("-");
  const lastIndex = slugArr[slugArr.length - 1];
  if (lastIndex && !countryCodes.includes(lastIndex)) {
    slugArr.push(countryCode);
    return slugArr.join("-");
  }
  return slug;
};

type Props = {
  promotedStrains: PromotedStrain[];
  country: string | null;
  coordinates: Coordinates | null;
  locationTitle: string;
  locationSlug: string;
};

type DealsState = {
  deals: Deal[];
  dealCategories: string[];
};

const DealsLocationPage: NextPage<Props> = ({
  coordinates,
  country,
  promotedStrains,
  locationTitle,
  locationSlug,
}) => {
  const domain = useDomain();
  const [deals, setDeals] = useState<DealsState>({
    dealCategories: [],
    deals: [],
  });
  const [promotions, setPromotions] = useState<Promotion[]>();
  const [dealsLoading, setDealsLoading] = useState(true);
  const [promotionsLoading, setPromotionsLoading] = useState(true);
  const isCanada = country === "ca";

  const loadDeals = async () => {
    if (!coordinates) {
      return;
    }

    const { deals: dealsData, dealCategories } = await getDeals(coordinates);

    setDealsLoading(false);
    setDeals({ dealCategories, deals: dealsData });
  };

  const loadPromotions = async () => {
    if (!coordinates) {
      return;
    }

    const { promotions: promotionsData } = await getPromotions(coordinates);
    setPromotionsLoading(false);
    setPromotions(promotionsData);
  };

  useEffect(() => {
    if (!coordinates) {
      return;
    }

    loadDeals();
    loadPromotions();
  }, [coordinates]);

  return (
    <div className="bg-white pb-xl">
      <Head>
        <title>
          {isCanada
            ? `${locationTitle} Cannabis Store Weed Deals | Leafly`
            : `${locationTitle} Dispensary Weed Deals | Leafly`}
        </title>
        <meta
          name="description"
          content={`Looking for weed deals in ${locationTitle}? Find special sales, promo codes, coupons, and discounts on flower, carts, edibles, and dabs. Get the best dispensaries deals in your neighborhood on Leafly`}
        ></meta>
        {country && (
          <link
            rel="canonical"
            href={`${domain}/deals/${formatSlug(locationSlug, country)}`}
          />
        )}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=yes"
        />
      </Head>
      <h1 data-testid="deals-header" className="text-lg container mt-lg mb-xl">
        {isCanada
          ? `Cannabis shop deals for ${locationTitle}`
          : `Dispensary deals in ${locationTitle}`}
      </h1>
      <section className="container">
        <DealsHero />
      </section>

      <section className="container py-xxl">
        <DealTypeCarousels
          isLoading={dealsLoading}
          categories={deals?.dealCategories}
          promotedStrains={promotedStrains}
        />
      </section>
      <section className="container py-xxl">
        <DealsResults dealsProp={deals?.deals} />
      </section>
      {!promotionsLoading && !!promotions?.length && (
        <section className="container py-xxl">
          <PromoResults promotionsProp={promotions} />
        </section>
      )}

      <section className="border-t border-light-grey pt-xxl">
        <div className="container" role="link" tabIndex={0}>
          <p className="text-sm m-none">
            <Link href="/deals/locations">See all locations</Link>
          </p>
        </div>

        <div className="container pt-xxl pb-lg">
          <DealsDisclaimer />
        </div>

        <Breadcrumbs>
          <Breadcrumb href="/deals">Deals</Breadcrumb>
          <Breadcrumb currentLocation href={`/deals/${locationSlug}`}>
            {locationTitle}
          </Breadcrumb>
        </Breadcrumbs>
      </section>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
}) => {
  try {
    const locationSlug = query.slug as string;

    const { city, state } = extractLocation(locationSlug);

    const [promotedStrains, { coordinates, countryCode }] = await Promise.all([
      getPromotedStrains(),
      getCityStateGeocodeServerSide({ city, state }).catch(() => {
        logError(
          `Failed to determine coordinates for ${city}, ${state} in /deals/[slug] getServersideProps`
        );

        return {
          coordinates: undefined,
          countryCode: undefined,
        };
      }),
    ]);

    if (!coordinates || !countryCode) {
      throw new InvalidLocationError(
        `could not geocode for slug: ${locationSlug}`
      );
    }

    return {
      props: {
        coordinates: coordinates ? coordinates : null,
        country: countryCode ? countryCode.toLowerCase() : null,
        locationSlug,
        locationTitle: `${city}, ${state}`,
        promotedStrains,
      },
    };
  } catch (err) {
    if (err instanceof InvalidLocationError) {
      return {
        notFound: true,
      };
    } else {
      throw err;
    }
  }
};

export default DealsLocationPage;
